import { AssetDoc, StreamInDocument } from '@livekatsomo/models';
import { YoutubePlayer } from '@livekatsomo/youtube';
import type {
  BitmovinPlayerProps,
  VimeoPlayerProps,
  YoutubePlayerProps,
} from '@livekatsomo/types';
import dynamic from 'next/dynamic';

/**
 * A dynamic component that loads the BitmovinPlayer from the @livekatsomo/web/ui-components/bitmovin package.
 */
const BitmovinPlayer = dynamic(
  () =>
    import('@livekatsomo/web/ui-components/bitmovin').then(
      (mod) => mod.BitmovinPlayer,
    ),
  {
    ssr: false,
  },
);

/**
 * A dynamic component that lazily loads the VimeoPlayer component from '@livekatsomo/web/ui-components/vimeo'.
 */
const VimeoPlayer = dynamic(
  () =>
    import('@livekatsomo/web/ui-components/vimeo').then(
      (mod) => mod.VimeoPlayer,
    ),
  {
    ssr: false,
  },
);

/**
 * An object containing player components for different video platforms.
 */
export const PlayerComponents = {
  bitmovin: BitmovinPlayer,
  vimeo: VimeoPlayer,
  youtube: YoutubePlayer,
} as const;

/**
 * Returns the player configuration object based on the stream type and poster asset.
 * @param stream - The stream object containing the stream type and URL.
 * @param poster - The poster asset object containing the download URLs and original URL.
 * @returns The player configuration object for either Vimeo or Bitmovin player.
 */
export function getPlayerConfig(
  stream: StreamInDocument,
  poster: AssetDoc | undefined,
) {
  switch (stream.streamType) {
    case 'vimeo':
      return {
        type: 'vimeo',
        source: { videoId: stream.url },
      } as VimeoPlayerProps;
    case 'youtube':
      return { type: 'youtube', source: stream.url } as YoutubePlayerProps;
    default:
      return {
        type: 'bitmovin',
        source: {
          hls: stream.url,
          poster: poster
            ? poster.downloadUrls?.jpeg1000x1000 || poster.originalUrl
            : undefined,
        },
      } as BitmovinPlayerProps;
  }
}
